import { CategoryButton } from 'app/components/category-group';
import { Box, Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigation } from 'app/hooks/navigation';
import { formatCurrency } from 'app/localization/i18n';
import type { CurrencyType } from 'app/schemas/user';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Category } from 'app/types/category';
import type { TransactionMode } from 'app/types/listing';
import type { LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isMobileNative } from 'app/utils/web-helpers';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react-native';
import PieChart from 'react-native-pie-chart';

const theme = createDSTheme();

export interface SavingsDataViewProps {
  chartMargin: number;
  containerMargin: number;
  dataViewMargin: number;
  deviceWidth: number;
  formattedTotal: string;
  graphSize: number;
  graphTitleFontSize: number;
  howItWorksVisible: boolean;
  savedPerCategory: { category: Category; amountSaved: number; value: number; color: string }[];
  setHowItWorksVisible: (visible: boolean) => void;
  transactionMode?: TransactionMode;
  type: LoanType;
  userCurrency: CurrencyType;
}

export function SavingsDataView({
  chartMargin,
  containerMargin,
  dataViewMargin,
  deviceWidth,
  formattedTotal,
  graphSize,
  graphTitleFontSize,
  howItWorksVisible,
  savedPerCategory,
  setHowItWorksVisible,
  transactionMode,
  type,
  userCurrency,
}: Readonly<SavingsDataViewProps>) {
  const { t } = useScopedTranslation();
  const navigation = useNavigation();
  const chartTopMargin = graphTitleFontSize * 1.1;

  return (
    <Box
      alignSelf="center"
      style={isMobileNative ? undefined : { marginHorizontal: 1.5 * containerMargin }}
      width={deviceWidth - 2 * containerMargin - 2 * dataViewMargin}
    >
      <Box marginBottom="spacing.general.x4" style={{ marginHorizontal: chartMargin }}>
        <PieChart cover={0.9} series={savedPerCategory} widthAndHeight={graphSize} />
      </Box>
      <Column
        left={chartMargin + graphSize * 0.1}
        position="absolute"
        top={theme.spacing['spacing.general.x4'] + graphSize / 2 - chartTopMargin}
        width={graphSize * 0.8}
      >
        <Text fontSize={graphTitleFontSize} textAlign="center">
          {formattedTotal}
        </Text>
        <Text fontSize={16} textAlign="center">
          {transactionMode ? t(`subtextByTransaction.${transactionMode}.${type}`) : t(`subtext.${type}`)}
        </Text>
      </Column>
      <Column>
        <Button onPress={() => setHowItWorksVisible(!howItWorksVisible)}>
          <Row gap="spacing.general.x0,5">
            {howItWorksVisible ? (
              <IconChevronUp strokeWidth={ICON_STROKE_WIDTH} />
            ) : (
              <IconChevronDown strokeWidth={ICON_STROKE_WIDTH} />
            )}
            <Text>{t('howItWorks.title')}</Text>
          </Row>
        </Button>
        {howItWorksVisible ? (
          <Text marginBottom="spacing.general.x2" textAlign="center">
            {transactionMode ? t(`howItWorks.bodyByTransaction.${transactionMode}`) : t('howItWorks.body')}
          </Text>
        ) : null}
      </Column>
      <Row flexWrap="wrap" justifyContent="center">
        {savedPerCategory.map(({ category, amountSaved }) => (
          <CategoryButton
            category={category}
            categoryNameSubstitute={formatCurrency(Math.round(amountSaved), userCurrency, 0)}
            key={category.id}
            marginHorizontal="spacing.general.x1"
            marginTop="spacing.general.x2"
            maxWidth={140}
            roundButton
            showIcon
          />
        ))}
      </Row>
      <Button
        marginBottom="spacing.general.x8"
        marginTop="spacing.general.x8"
        onPress={() => {
          navigation.navigate('management', {
            screen: 'past-loans',
            params: { type, ...(transactionMode ? { transactionMode } : {}) },
            initial: false,
          });
        }}
        variant="secondary"
      >
        {transactionMode
          ? t(`ctaButtonTextByTransaction.${transactionMode}.${type}`)
          : t(`ctaButtonText.${type as string}`)}
      </Button>
    </Box>
  );
}
